/**
 * DebugMode — centralised toggle for hiding/showing sensitive admin actions.
 *
 * The DEBUG mode is a PURELY FRONTEND flag (backend is untouched).
 * It is persisted in localStorage under the key `YGO_DEBUG_MODE`.
 *
 * Auto-expiration: each activation records a timestamp in `YGO_DEBUG_MODE_SINCE`.
 * After DEBUG_TTL_MS (default 1 hour) the flag self-disables on next read.
 * This prevents admins from leaving DEBUG ON indefinitely.
 *
 * Usage:
 *   import { useDebugMode, isDebugModeEnabled } from 'hooks/useDebugMode';
 *
 *   const { debugMode, setDebugMode, expiresInMs } = useDebugMode();
 *   if (!debugMode) return null;
 *
 * The toggle UI lives in Admin > Paramètres > Développeur.
 * A persistent banner (DebugModeBanner) is rendered at the top of every
 * admin page when DEBUG is ON.
 */
import { useCallback, useEffect, useState } from 'react';

export const DEBUG_MODE_KEY = 'YGO_DEBUG_MODE';
export const DEBUG_MODE_SINCE_KEY = 'YGO_DEBUG_MODE_SINCE';
export const DEBUG_MODE_EVENT = 'ygo-debug-mode-change';
export const DEBUG_TTL_MS = 60 * 60 * 1000; // 1 hour

const _readRaw = () => {
  try {
    return {
      enabled: localStorage.getItem(DEBUG_MODE_KEY) === 'true',
      since: parseInt(localStorage.getItem(DEBUG_MODE_SINCE_KEY) || '0', 10) || 0,
    };
  } catch {
    return { enabled: false, since: 0 };
  }
};

const _writeRaw = (enabled, since) => {
  try {
    localStorage.setItem(DEBUG_MODE_KEY, enabled ? 'true' : 'false');
    if (enabled) {
      localStorage.setItem(DEBUG_MODE_SINCE_KEY, String(since || Date.now()));
    } else {
      localStorage.removeItem(DEBUG_MODE_SINCE_KEY);
    }
  } catch {
    // ignore (private mode, SSR)
  }
};

/**
 * One-shot helper — returns true only if DEBUG is ON AND not expired.
 * If expired, auto-disables the flag and returns false.
 */
export const isDebugModeEnabled = () => {
  const { enabled, since } = _readRaw();
  if (!enabled) return false;
  const age = Date.now() - since;
  if (since > 0 && age > DEBUG_TTL_MS) {
    // expired → self-disable
    _writeRaw(false, 0);
    try { window.dispatchEvent(new CustomEvent(DEBUG_MODE_EVENT, { detail: false })); } catch { /* noop */ }
    return false;
  }
  return true;
};

/** Remaining TTL in ms. Returns 0 if expired or OFF. */
export const getDebugModeRemainingMs = () => {
  const { enabled, since } = _readRaw();
  if (!enabled || since <= 0) return 0;
  const remaining = DEBUG_TTL_MS - (Date.now() - since);
  return remaining > 0 ? remaining : 0;
};

/** Set the flag and broadcast an event so all components update. */
export const setDebugModeValue = (enabled) => {
  _writeRaw(!!enabled, enabled ? Date.now() : 0);
  try {
    window.dispatchEvent(new CustomEvent(DEBUG_MODE_EVENT, { detail: !!enabled }));
  } catch {
    // ignore
  }
};

/**
 * React hook — returns `{ debugMode, setDebugMode, expiresInMs }` and subscribes
 * to changes from any other component/tab. Ticks every 30s to auto-expire.
 */
export const useDebugMode = () => {
  const [debugMode, setDebugModeState] = useState(() => isDebugModeEnabled());
  const [expiresInMs, setExpiresInMs] = useState(() => getDebugModeRemainingMs());

  useEffect(() => {
    const onChange = (e) => {
      setDebugModeState(!!e.detail);
      setExpiresInMs(getDebugModeRemainingMs());
    };
    const onStorage = (e) => {
      if (e.key === DEBUG_MODE_KEY) {
        setDebugModeState(isDebugModeEnabled());
        setExpiresInMs(getDebugModeRemainingMs());
      }
    };
    window.addEventListener(DEBUG_MODE_EVENT, onChange);
    window.addEventListener('storage', onStorage);

    // Tick every 30s: update remaining + auto-disable if expired
    const interval = setInterval(() => {
      const stillOn = isDebugModeEnabled();
      setDebugModeState(stillOn);
      setExpiresInMs(getDebugModeRemainingMs());
    }, 30_000);

    return () => {
      window.removeEventListener(DEBUG_MODE_EVENT, onChange);
      window.removeEventListener('storage', onStorage);
      clearInterval(interval);
    };
  }, []);

  const setDebugMode = useCallback((enabled) => {
    setDebugModeValue(enabled);
    setDebugModeState(!!enabled);
    setExpiresInMs(enabled ? DEBUG_TTL_MS : 0);
  }, []);

  return { debugMode, setDebugMode, expiresInMs };
};

export default useDebugMode;
